// The synced tables, mirroring supabase/migrations exactly. The drift check
// (supabase/tests/07_registry.test.sql, generated by scripts/gen-pgtap-schema.mjs)
// fails if a column's name, type or nullability differs from Postgres.
// Order matters: it is the FK-safe apply order (sync_table_config.apply_order).
import { c, defineTable } from './columns.ts'

export const billingCounters = defineTable('billing_counters', {
  code: c.text(),
  name: c.text(),
  kind: c.text().oneOf('device', 'web'),
  installation_id: c.text().orNull(),
  is_active: c.bool().withDefault(),
  created_by: c.uuid().orNull().serverSet(),
})

export const counterSequences = defineTable('counter_sequences', {
  counter_id: c.uuid(),
  doc_type: c
    .text()
    .oneOf(
      'sales_invoice',
      'credit_note',
      'quotation',
      'sales_order',
      'purchase_order',
      'purchase_request',
    ),
  fiscal_year: c.text(),
  next_seq: c.int().withDefault(),
})

export const tenantSettings = defineTable('tenant_settings', {
  business_name: c.text().withDefault(),
  legal_name: c.text().orNull(),
  pan_vat_number: c.text().orNull(),
  address: c.text().orNull(),
  phone: c.text().orNull(),
  email: c.text().orNull(),
  currency: c.text().withDefault(),
  currency_symbol: c.text().withDefault(),
  fiscal_calendar: c.text().oneOf('bs', 'ad').withDefault(),
  /** How dates are shown: AD, BS or both (display only; the fiscal year is above). */
  date_display: c.text().oneOf('bs', 'ad', 'both').withDefault(),
  fiscal_year_start_month: c.int().pg('smallint').withDefault(),
  fiscal_year_start_day: c.int().pg('smallint').withDefault(),
  invoice_prefix: c.text().withDefault(),
  credit_note_prefix: c.text().withDefault(),
  quotation_prefix: c.text().withDefault(),
  sales_order_prefix: c.text().withDefault(),
  purchase_order_prefix: c.text().withDefault(),
  purchase_request_prefix: c.text().withDefault(),
  /** A new quotation is valid for this many days. */
  quote_valid_days: c.int().withDefault(),
  /** Printed under every quotation unless it has its own terms. */
  quote_terms: c.text().orNull(),
  default_tax_rate_id: c.uuid().orNull(),
  prices_include_tax: c.bool().withDefault(),
  allow_negative_stock: c.bool().withDefault(),
  invoice_footer: c.text().orNull(),
  business_type: c
    .text()
    .oneOf(
      'general',
      'kirana',
      'pharmacy',
      'hardware',
      'apparel',
      'electronics',
      'cosmetics',
      'bakery',
      'wholesale',
      'restaurant',
      'workshop',
    )
    .withDefault(),
  /** Enabled product field groups; null = the business type's defaults. */
  field_groups: c.json().orNull(),
  /** The business's own product fields: [{ key, label, type }]. */
  custom_fields: c.json().withDefault(),
  /** Batches expiring within this many days show as "expiring soon". */
  expiry_alert_days: c.int().withDefault(),
  /** Selling a batch after its expiry date: warn, or block. */
  expired_sale: c.text().oneOf('warn', 'block').withDefault(),
  min_price_rule: c.text().oneOf('warn', 'block').withDefault(),
  /** Weighing-scale barcode format (see core ScaleConfig). Display/parsing only. */
  scale_barcode: c.json().orNull(),
  /** Registered for PAN only or for VAT: the bill's title and labels (display only). */
  tax_registration: c.text().oneOf('pan', 'vat').withDefault(),
  /** Who may bill a line without a product (a custom item). */
  custom_items: c.text().oneOf('everyone', 'managers', 'off').withDefault(),
  /** Restaurant mode: tables and open orders (a mixed business may switch it on). */
  restaurant: c.bool().withDefault(),
  /** Print kitchen tickets when they are sent. */
  kot_print: c.bool().withDefault(),
  /** Restaurant alerts: how long one waits for its table's server before everyone hears it. */
  alert_wait_call_seconds: c.int().withDefault(),
  alert_wait_ready_seconds: c.int().withDefault(),
  /** Ready-made notes for order items: ["Less spicy", …]. Display only. */
  item_notes: c.json().withDefault(),
  /** Guests order from the table's QR code (a waiter accepts each request). */
  guest_ordering: c.bool().withDefault(),
  /** The one colour of the guest's menu page. */
  guest_accent: c.text().oneOf('green', 'terracotta', 'indigo', 'saffron').withDefault(),
  /** The "Delivery charge" product: delivery is on when set; its price is the default fee. */
  delivery_product_id: c.uuid().orNull(),
  /** Email owners and admins when items newly run low (daily cron). */
  low_stock_email: c.bool().withDefault(),
  /** What the business's payment QR says (its text, redrawn on every device); null = none. */
  payment_qr: c.text().orNull(),
  /** Shown above the QR: "Fonepay", "eSewa". */
  payment_qr_label: c.text().orNull(),
  /** Shown under the QR, so the customer can check who they are paying. */
  payment_qr_payee: c.text().orNull(),
  /** The payment method a QR payment is recorded as. */
  payment_qr_method: c.text().oneOf('wallet', 'bank').withDefault(),
  /** When and by whom the QR was last set (a server trigger; devices cannot write them). */
  payment_qr_set_at: c.timestamptz().orNull().serverSet(),
  payment_qr_set_by: c.uuid().orNull().serverSet(),
  /** The business's logo on bills: a small PNG data URL made on the device (lib/logo.ts). */
  logo: c.text().orNull(),
  /** Whether 80 mm receipts print the logo too. */
  logo_on_receipt: c.bool().withDefault(),
})

/** One step of approval: from an amount, by a role or above (docs/plans/po-approvals.md). */
export const approvalRules = defineTable('approval_rules', {
  doc_type: c.text().oneOf('purchase_order', 'requisition', 'supplier_bill').withDefault(),
  level: c.int(),
  min_amount_paisa: c.bigint().withDefault(),
  approver_role: c.text().oneOf('manager', 'admin', 'owner').withDefault(),
})

export const taxRates = defineTable('tax_rates', {
  name: c.text(),
  rate_bp: c.int(),
  is_active: c.bool().withDefault(),
})

export const units = defineTable('units', {
  name: c.text(),
  symbol: c.text(),
  allows_decimal: c.bool().withDefault(),
})

export const brands = defineTable('brands', {
  name: c.text(),
})

export const categories = defineTable('categories', {
  name: c.text(),
  parent_id: c.uuid().orNull(),
  /** The order of a menu's sections on the guest's page (then by name). */
  sort_order: c.int().withDefault(),
})

export const customers = defineTable('customers', {
  name: c.text(),
  contact_name: c.text().orNull(),
  email: c.text().orNull(),
  phone: c.text().orNull(),
  address: c.text().orNull(),
  pan_vat_number: c.text().orNull(),
  credit_limit_paisa: c.bigint().orNull(),
  notes: c.text().orNull(),
  is_active: c.bool().withDefault(),
  price_tier: c.text().oneOf('retail', 'wholesale').withDefault(),
})

export const locations = defineTable('locations', {
  name: c.text(),
  type: c.text().oneOf('warehouse', 'store', 'transit').withDefault(),
  address: c.text().orNull(),
  is_active: c.bool().withDefault(),
})

export const suppliers = defineTable('suppliers', {
  name: c.text(),
  contact_name: c.text().orNull(),
  email: c.text().orNull(),
  phone: c.text().orNull(),
  address: c.text().orNull(),
  pan_vat_number: c.text().orNull(),
  notes: c.text().orNull(),
  is_active: c.bool().withDefault(),
})

/** Where tables stand: floor, rooftop. */
export const diningAreas = defineTable('dining_areas', {
  name: c.text(),
  sort_order: c.int().withDefault(),
})

/** A table. It is free when it has no open order (its state is never stored). */
export const diningTables = defineTable('dining_tables', {
  area_id: c.uuid().orNull(),
  name: c.text(),
  seats: c.int().orNull(),
  sort_order: c.int().withDefault(),
  is_active: c.bool().withDefault(),
})

export const products = defineTable('products', {
  sku: c.text(),
  name: c.text(),
  barcode: c.text().orNull(),
  description: c.text().orNull(),
  category_id: c.uuid().orNull(),
  unit_id: c.uuid().orNull(),
  tax_rate_id: c.uuid().orNull(),
  supplier_id: c.uuid().orNull(),
  sell_price_paisa: c.bigint().withDefault(),
  cost_price_paisa: c.bigint().orNull(),
  price_includes_tax: c.bool().withDefault(),
  track_stock: c.bool().withDefault(),
  min_stock_milli: c.bigint().withDefault(),
  reorder_point_milli: c.bigint().withDefault(),
  image_path: c.text().orNull(),
  is_active: c.bool().withDefault(),
  brand_id: c.uuid().orNull(),
  manufacturer: c.text().orNull(),
  mrp_paisa: c.bigint().orNull(),
  hs_code: c.text().orNull(),
  shelf_location: c.text().orNull(),
  tags: c.text().orNull(),
  reorder_qty_milli: c.bigint().withDefault(),
  /** Display-only field-group and custom values ({ generic_name: "…" }). */
  attributes: c.json().orNull(),
  track_batches: c.bool().withDefault(),
  track_expiry: c.bool().withDefault(),
  shelf_life_days: c.int().orNull(),
  shelf_life_unit: c.text().oneOf('day', 'month', 'year').orNull(),
  /** standard, a variant group (never stocked or sold) or one of its variants. */
  kind: c.text().oneOf('standard', 'group', 'variant').withDefault(),
  parent_id: c.uuid().orNull(),
  /** On a group: the options and their values ({ Size: ["S", "M"] }). Display only. */
  variant_options: c.json().orNull(),
  /** On a variant: its values ({ Size: "M" }). Display only. */
  variant_values: c.json().orNull(),
  track_serials: c.bool().withDefault(),
  warranty_months: c.int().orNull(),
  wholesale_price_paisa: c.bigint().orNull(),
  /** Lowest price staff may sell at (see tenant_settings.min_price_rule). */
  min_price_paisa: c.bigint().orNull(),
  /** Item code programmed into the weighing scale. */
  plu_code: c.text().orNull(),
  /** Restaurant mode: where an order of this goes (none = no kitchen ticket). */
  kitchen_station: c.text().oneOf('kitchen', 'bar', 'none').withDefault(),
  /** Sold to customers (on the menu). False: an ingredient, kept in stock but never billed. */
  for_sale: c.bool().withDefault(),
  /** The mark beside a dish on the guest's menu; null shows none. Display only. */
  diet: c.text().oneOf('veg', 'non_veg', 'egg').orNull(),
  /** "Spicy" beside a dish on the guest's menu. Display only. */
  spicy: c.bool().withDefault(),
  /** Restaurant mode: sold out until then (the next 05:00 when marked); null is on the menu. */
  sold_out_until: c.timestamptz().orNull(),
})

/** Extra barcodes and pack sizes of a product. */
export const productCodes = defineTable('product_codes', {
  product_id: c.uuid(),
  code: c.text(),
  label: c.text().orNull(),
  qty_milli: c.bigint().withDefault(),
  sell_price_paisa: c.bigint().orNull(),
})

/** A recipe line: one unit of `product_id` uses `qty_milli` of the ingredient `component_id`. */
export const productComponents = defineTable('product_components', {
  product_id: c.uuid(),
  component_id: c.uuid(),
  qty_milli: c.bigint(),
})

/** A batch of a product (id = batchId(tenant, product, batch_no), see core). */
export const batches = defineTable('batches', {
  product_id: c.uuid(),
  batch_no: c.text(),
  mfg_date: c.date().orNull(),
  expiry_date: c.date().orNull(),
  mrp_paisa: c.bigint().orNull(),
  cost_paisa: c.bigint().orNull(),
  supplier_id: c.uuid().orNull(),
  notes: c.text().orNull(),
})

/** One unit of a serial-tracked product (id = serialId(tenant, product, serial), see core). */
export const productSerials = defineTable('product_serials', {
  product_id: c.uuid(),
  serial: c.text(),
  status: c.text().oneOf('in_stock', 'sold', 'written_off').withDefault(),
  location_id: c.uuid().orNull(),
  receipt_line_id: c.uuid().orNull(),
  sold_line_id: c.uuid().orNull(),
  returned_line_id: c.uuid().orNull(),
  warranty_until: c.date().orNull(),
  notes: c.text().orNull(),
})

/**
 * An order placed with a supplier. Paperwork only: stock arrives on a goods
 * receipt, and what has arrived is summed from the receipt lines, never stored.
 */
/** Someone asks for stock (purchasing phase 3); approved lines become purchase order lines. */
export const purchaseRequests = defineTable('purchase_requests', {
  location_id: c.uuid().orNull(),
  status: c
    .text()
    .oneOf('draft', 'submitted', 'approved', 'rejected', 'closed', 'cancelled')
    .withDefault(),
  counter_id: c.uuid().orNull(),
  fiscal_year: c.text().orNull(),
  seq: c.int().orNull(),
  number: c.text().orNull(),
  request_date: c.date().withDefault(),
  needed_by: c.date().orNull(),
  notes: c.text().orNull(),
  /** The estimate: the sum of the lines. */
  total_paisa: c.bigint().withDefault(),
  closed_at: c.timestamptz().orNull(),
  close_reason: c.text().orNull(),
  created_by: c.uuid().orNull().serverSet(),
})

export const purchaseRequestLines = defineTable('purchase_request_lines', {
  purchase_request_id: c.uuid(),
  line_no: c.int(),
  /** Null: written in (`description`), matched to a product before it is ordered. */
  product_id: c.uuid().orNull(),
  description: c.text().orNull(),
  qty_milli: c.bigint(),
  est_cost_paisa: c.bigint().withDefault(),
})

export const purchaseOrders = defineTable('purchase_orders', {
  supplier_id: c.uuid().orNull(),
  location_id: c.uuid().orNull(),
  status: c
    .text()
    .oneOf('draft', 'submitted', 'approved', 'issued', 'sent', 'rejected', 'closed', 'cancelled')
    .withDefault(),
  counter_id: c.uuid().orNull(),
  fiscal_year: c.text().orNull(),
  seq: c.int().orNull(),
  number: c.text().orNull(),
  closed_at: c.timestamptz().orNull(),
  close_reason: c.text().orNull(),
  order_date: c.date().withDefault(),
  expected_date: c.date().orNull(),
  notes: c.text().orNull(),
  total_paisa: c.bigint().withDefault(),
  created_by: c.uuid().orNull().serverSet(),
})

export const purchaseOrderLines = defineTable('purchase_order_lines', {
  purchase_order_id: c.uuid(),
  line_no: c.int(),
  product_id: c.uuid(),
  qty_ordered_milli: c.bigint(),
  unit_cost_paisa: c.bigint().withDefault(),
  /** Made from this purchase request line. */
  purchase_request_line_id: c.uuid().orNull(),
})

export const goodsReceipts = defineTable('goods_receipts', {
  purchase_order_id: c.uuid().orNull(),
  supplier_id: c.uuid().orNull(),
  location_id: c.uuid(),
  receipt_date: c.date().withDefault(),
  reference: c.text().orNull(),
  notes: c.text().orNull(),
  created_by: c.uuid().orNull().serverSet(),
})

export const goodsReceiptLines = defineTable('goods_receipt_lines', {
  goods_receipt_id: c.uuid(),
  purchase_order_line_id: c.uuid().orNull(),
  line_no: c.int(),
  product_id: c.uuid(),
  qty_milli: c.bigint(),
  unit_cost_paisa: c.bigint().withDefault(),
  batch_id: c.uuid().orNull(),
})

/**
 * A quotation or a sales order (`kind`). Paperwork only: stock moves on the
 * invoice made from it, and how much has been invoiced is summed from the
 * invoice lines that point back here, never stored.
 */
/** One decision on a document; written only by decide_document on the server. */
export const documentApprovals = defineTable('document_approvals', {
  doc_type: c.text().oneOf('purchase_order', 'requisition', 'supplier_bill'),
  doc_id: c.uuid(),
  level: c.int(),
  decision: c.text().oneOf('approved', 'rejected'),
  note: c.text().orNull(),
  decided_by: c.uuid().orNull().serverSet(),
})

export const salesOrders = defineTable('sales_orders', {
  kind: c.text().oneOf('quotation', 'order'),
  status: c
    .text()
    .oneOf('draft', 'sent', 'accepted', 'declined', 'confirmed', 'closed', 'cancelled')
    .withDefault(),
  counter_id: c.uuid().orNull(),
  fiscal_year: c.text().orNull(),
  seq: c.int().orNull(),
  number: c.text().orNull(),
  doc_date: c.date().withDefault(),
  valid_until: c.date().orNull(),
  expected_date: c.date().orNull(),
  customer_id: c.uuid().orNull(),
  customer_name: c.text().orNull(),
  customer_pan: c.text().orNull(),
  customer_address: c.text().orNull(),
  location_id: c.uuid().orNull(),
  currency: c.text().withDefault(),
  subtotal_paisa: c.bigint().withDefault(),
  discount_paisa: c.bigint().withDefault(),
  taxable_paisa: c.bigint().withDefault(),
  tax_paisa: c.bigint().withDefault(),
  round_off_paisa: c.bigint().withDefault(),
  total_paisa: c.bigint().withDefault(),
  notes: c.text().orNull(),
  terms: c.text().orNull(),
  /** The quotation an order came from, or the document this one revises. */
  source_id: c.uuid().orNull(),
  closed_at: c.timestamptz().orNull(),
  close_reason: c.text().orNull(),
  created_by: c.uuid().orNull().serverSet(),
})

export const salesOrderLines = defineTable('sales_order_lines', {
  sales_order_id: c.uuid(),
  line_no: c.int(),
  product_id: c.uuid().orNull(),
  description: c.text(),
  qty_milli: c.bigint(),
  unit_price_paisa: c.bigint(),
  price_includes_tax: c.bool().withDefault(),
  gross_paisa: c.bigint(),
  discount_paisa: c.bigint().withDefault(),
  tax_rate_id: c.uuid().orNull(),
  tax_rate_bp: c.int().withDefault(),
  taxable_paisa: c.bigint(),
  tax_paisa: c.bigint(),
  total_paisa: c.bigint(),
})

export const salesInvoices = defineTable('sales_invoices', {
  status: c.text().oneOf('draft', 'finalized', 'cancelled').withDefault(),
  counter_id: c.uuid().orNull(),
  fiscal_year: c.text().orNull(),
  seq: c.int().orNull(),
  number: c.text().orNull(),
  invoice_date: c.date().withDefault(),
  due_date: c.date().orNull(),
  customer_id: c.uuid().orNull(),
  customer_name: c.text().orNull(),
  customer_pan: c.text().orNull(),
  customer_address: c.text().orNull(),
  location_id: c.uuid().orNull(),
  currency: c.text().withDefault(),
  subtotal_paisa: c.bigint().withDefault(),
  discount_paisa: c.bigint().withDefault(),
  taxable_paisa: c.bigint().withDefault(),
  tax_paisa: c.bigint().withDefault(),
  round_off_paisa: c.bigint().withDefault(),
  total_paisa: c.bigint().withDefault(),
  notes: c.text().orNull(),
  finalized_at: c.timestamptz().orNull(),
  cancelled_at: c.timestamptz().orNull(),
  cancel_reason: c.text().orNull(),
  /** The quotation or order this bill was made from. */
  sales_order_id: c.uuid().orNull(),
  ird_status: c
    .text()
    .oneOf('not_applicable', 'pending', 'reported', 'failed')
    .withDefault()
    .serverSet(),
  created_by: c.uuid().orNull().serverSet(),
})

export const salesInvoiceLines = defineTable('sales_invoice_lines', {
  invoice_id: c.uuid(),
  line_no: c.int(),
  product_id: c.uuid().orNull(),
  description: c.text(),
  qty_milli: c.bigint(),
  unit_price_paisa: c.bigint(),
  price_includes_tax: c.bool().withDefault(),
  gross_paisa: c.bigint(),
  discount_paisa: c.bigint().withDefault(),
  tax_rate_id: c.uuid().orNull(),
  tax_rate_bp: c.int().withDefault(),
  taxable_paisa: c.bigint(),
  tax_paisa: c.bigint(),
  total_paisa: c.bigint(),
  batch_id: c.uuid().orNull(),
  /** Serial numbers this line covered (display only; the register is product_serials). */
  serials: c.json().orNull(),
  /** The order line this bill line fills. */
  sales_order_line_id: c.uuid().orNull(),
})

export const creditNotes = defineTable('credit_notes', {
  invoice_id: c.uuid(),
  status: c.text().oneOf('draft', 'finalized', 'cancelled').withDefault(),
  counter_id: c.uuid().orNull(),
  fiscal_year: c.text().orNull(),
  seq: c.int().orNull(),
  number: c.text().orNull(),
  note_date: c.date().withDefault(),
  reason: c.text(),
  restock: c.bool().withDefault(),
  location_id: c.uuid().orNull(),
  subtotal_paisa: c.bigint().withDefault(),
  discount_paisa: c.bigint().withDefault(),
  taxable_paisa: c.bigint().withDefault(),
  tax_paisa: c.bigint().withDefault(),
  round_off_paisa: c.bigint().withDefault(),
  total_paisa: c.bigint().withDefault(),
  finalized_at: c.timestamptz().orNull(),
  cancelled_at: c.timestamptz().orNull(),
  cancel_reason: c.text().orNull(),
  created_by: c.uuid().orNull().serverSet(),
})

export const creditNoteLines = defineTable('credit_note_lines', {
  credit_note_id: c.uuid(),
  invoice_line_id: c.uuid().orNull(),
  line_no: c.int(),
  product_id: c.uuid().orNull(),
  description: c.text(),
  qty_milli: c.bigint(),
  unit_price_paisa: c.bigint(),
  price_includes_tax: c.bool().withDefault(),
  gross_paisa: c.bigint(),
  discount_paisa: c.bigint().withDefault(),
  tax_rate_id: c.uuid().orNull(),
  tax_rate_bp: c.int().withDefault(),
  taxable_paisa: c.bigint(),
  tax_paisa: c.bigint(),
  total_paisa: c.bigint(),
  batch_id: c.uuid().orNull(),
  /** Serial numbers this line covered (display only; the register is product_serials). */
  serials: c.json().orNull(),
})

/**
 * A product assigned to a custom (product-less) line of a finalized invoice.
 * The bill is unchanged; the stock goes out with 'sale' movements of the invoice.
 */
export const invoiceLineProducts = defineTable('invoice_line_products', {
  invoice_id: c.uuid(),
  invoice_line_id: c.uuid(),
  product_id: c.uuid(),
  qty_milli: c.bigint(),
  location_id: c.uuid(),
  batch_id: c.uuid().orNull(),
  created_by: c.uuid().orNull().serverSet(),
})

/**
 * One print of an issued bill (docs/plans/print-copies.md). Insert-only; copy_no is
 * what the paper said: 0 the original, N "Copy of original - N" (not unique).
 */
export const invoicePrints = defineTable('invoice_prints', {
  invoice_id: c.uuid(),
  format: c.text().oneOf('a4', 'receipt'),
  copy_no: c.int(),
  printed_at: c.timestamptz(),
  created_by: c.uuid().orNull().serverSet(),
})

/**
 * An open bill on a table. Checkout turns it into a finalized invoice and closes
 * it in the same tx; a merged order is void with merged_into_id set.
 */
export const orders = defineTable('orders', {
  table_id: c.uuid().orNull(),
  status: c.text().oneOf('open', 'billed', 'closed', 'void').withDefault(),
  covers: c.int().withDefault(),
  customer_id: c.uuid().orNull(),
  notes: c.text().orNull(),
  invoice_id: c.uuid().orNull(),
  merged_into_id: c.uuid().orNull(),
  opened_at: c.timestamptz().withDefault(),
  closed_at: c.timestamptz().orNull(),
  void_reason: c.text().orNull(),
  service_type: c.text().oneOf('dine_in', 'takeaway', 'delivery').withDefault(),
  order_no: c.int().orNull(),
  label: c.text().orNull(),
  /** Who looks after the order: whoever opened it, unless it was given to someone else. */
  server_id: c.uuid().orNull(),
  created_by: c.uuid().orNull().serverSet(),
})

/** Append-only. A cancelled item is a new row with a negative quantity and void_of_id. */
export const orderItems = defineTable('order_items', {
  order_id: c.uuid(),
  product_id: c.uuid().orNull(),
  description: c.text(),
  qty_milli: c.bigint(),
  unit_price_paisa: c.bigint(),
  price_includes_tax: c.bool().withDefault(),
  tax_rate_id: c.uuid().orNull(),
  tax_rate_bp: c.int().withDefault(),
  note: c.text().orNull(),
  void_of_id: c.uuid().orNull(),
  void_reason: c.text().orNull(),
  created_by: c.uuid().orNull().serverSet(),
  /** The product's kitchen station when it was ordered. */
  station: c.text().oneOf('kitchen', 'bar', 'none').withDefault(),
})

/** A kitchen ticket (KOT): what one "Send to kitchen" asked a station to make. */
export const kotTickets = defineTable('kot_tickets', {
  order_id: c.uuid(),
  station: c.text().oneOf('kitchen', 'bar'),
  /** A short number for the kitchen, restarting each day (not a document number). */
  kot_no: c.int(),
  status: c.text().oneOf('new', 'preparing', 'ready').withDefault(),
  sent_at: c.timestamptz().withDefault(),
  ready_at: c.timestamptz().orNull(),
  created_by: c.uuid().orNull().serverSet(),
  /** When the kitchen started on it. */
  started_at: c.timestamptz().orNull(),
})

/** Insert-only: an order_items row (an item or its cancellation) a ticket covers. */
export const kotItems = defineTable('kot_items', {
  kot_id: c.uuid(),
  order_item_id: c.uuid(),
})

/**
 * Where a delivery order goes and what became of it. Address, phone, note and
 * rider change while pending; after it leaves (with its order checked out)
 * only the outcome is recorded, and delivered or failed never change.
 */
export const deliveries = defineTable('deliveries', {
  order_id: c.uuid(),
  address: c.text(),
  phone: c.text().orNull(),
  note: c.text().orNull(),
  /** Free text: riders often have no login. */
  rider_name: c.text().orNull(),
  status: c.text().oneOf('pending', 'out', 'delivered', 'failed').withDefault(),
  dispatched_at: c.timestamptz().orNull(),
  delivered_at: c.timestamptz().orNull(),
  failed_reason: c.text().orNull(),
  created_by: c.uuid().orNull().serverSet(),
})

/**
 * A guest's request from a table's QR code: an order, a call for the waiter or
 * the bill. Made by the server only; a device decides a pending one.
 */
export const orderRequests = defineTable('order_requests', {
  table_id: c.uuid(),
  /** Null while a request sent to a free table waits to be accepted. */
  order_id: c.uuid().orNull(),
  kind: c.text().oneOf('order', 'call', 'bill').withDefault(),
  status: c.text().oneOf('pending', 'accepted', 'rejected', 'cancelled').withDefault(),
  client_ref: c.text().orNull(),
  decided_at: c.timestamptz().orNull(),
  decided_by: c.uuid().orNull().serverSet(),
  /** Goes up each time the guests change a waiting round; a device never changes it. */
  revision: c.int().withDefault(),
})

/** What a guest asked for (never written by a device). */
export const orderRequestItems = defineTable('order_request_items', {
  request_id: c.uuid(),
  product_id: c.uuid(),
  description: c.text(),
  qty_milli: c.bigint(),
  unit_price_paisa: c.bigint(),
  note: c.text().orNull(),
})

export const payments = defineTable('payments', {
  customer_id: c.uuid().orNull(),
  payment_date: c.date().withDefault(),
  method: c.text().oneOf('cash', 'bank', 'card', 'wallet', 'cheque', 'other'),
  amount_paisa: c.bigint(),
  reference: c.text().orNull(),
  notes: c.text().orNull(),
  status: c.text().oneOf('active', 'void').withDefault(),
  voided_at: c.timestamptz().orNull(),
  void_reason: c.text().orNull(),
  created_by: c.uuid().orNull().serverSet(),
})

export const paymentAllocations = defineTable('payment_allocations', {
  payment_id: c.uuid(),
  invoice_id: c.uuid(),
  amount_paisa: c.bigint(),
})

/**
 * A supplier's bill: what they charged, beside the receipt it came with (a receipt is
 * insert-only, so the bill is its own row). No receipt = an old balance or a charge that
 * brought no stock. What is owed is summed from allocations, never stored.
 */
export const supplierBills = defineTable('supplier_bills', {
  supplier_id: c.uuid(),
  goods_receipt_id: c.uuid().orNull(),
  bill_no: c.text().orNull(),
  bill_date: c.date().withDefault(),
  due_date: c.date().orNull(),
  total_paisa: c.bigint(),
  notes: c.text().orNull(),
  status: c.text().oneOf('active', 'void').withDefault(),
  voided_at: c.timestamptz().orNull(),
  void_reason: c.text().orNull(),
  created_by: c.uuid().orNull().serverSet(),
})

/** Money paid to a supplier. Voided, never edited, like `payments`. */
export const supplierPayments = defineTable('supplier_payments', {
  supplier_id: c.uuid(),
  payment_date: c.date().withDefault(),
  method: c.text().oneOf('cash', 'bank', 'card', 'wallet', 'cheque', 'other'),
  amount_paisa: c.bigint(),
  reference: c.text().orNull(),
  notes: c.text().orNull(),
  status: c.text().oneOf('active', 'void').withDefault(),
  voided_at: c.timestamptz().orNull(),
  void_reason: c.text().orNull(),
  created_by: c.uuid().orNull().serverSet(),
})

/** Credit from a supplier: goods sent back (with lines) or a corrected price (none). */
export const supplierCredits = defineTable('supplier_credits', {
  supplier_id: c.uuid(),
  goods_receipt_id: c.uuid().orNull(),
  credit_no: c.text().orNull(),
  credit_date: c.date().withDefault(),
  total_paisa: c.bigint(),
  reason: c.text().orNull(),
  notes: c.text().orNull(),
  status: c.text().oneOf('active', 'void').withDefault(),
  voided_at: c.timestamptz().orNull(),
  void_reason: c.text().orNull(),
  created_by: c.uuid().orNull().serverSet(),
})

export const supplierCreditLines = defineTable('supplier_credit_lines', {
  supplier_credit_id: c.uuid(),
  line_no: c.int(),
  product_id: c.uuid(),
  qty_milli: c.bigint(),
  unit_cost_paisa: c.bigint().withDefault(),
  batch_id: c.uuid().orNull(),
})

/** A payment's or a credit's share set against one bill (exactly one of the two). */
export const supplierAllocations = defineTable('supplier_allocations', {
  bill_id: c.uuid(),
  payment_id: c.uuid().orNull(),
  credit_id: c.uuid().orNull(),
  amount_paisa: c.bigint(),
})

export const stockMovements = defineTable('stock_movements', {
  product_id: c.uuid(),
  location_id: c.uuid(),
  type: c
    .text()
    .oneOf(
      'opening',
      'receipt',
      'sale',
      'sale_return',
      'adjustment',
      'transfer_in',
      'transfer_out',
      'count',
      'purchase_return',
    ),
  qty_milli: c.bigint(),
  unit_cost_paisa: c.bigint().orNull(),
  ref_type: c
    .text()
    .oneOf(
      'sales_invoice',
      'credit_note',
      'goods_receipt',
      'transfer',
      'count',
      'manual',
      'supplier_credit',
    )
    .orNull(),
  ref_id: c.uuid().orNull(),
  notes: c.text().orNull(),
  occurred_at: c.timestamptz().withDefault(),
  created_by: c.uuid().orNull().serverSet(),
  batch_id: c.uuid().orNull(),
})

export const syncedTables = [
  billingCounters,
  counterSequences,
  tenantSettings,
  approvalRules,
  taxRates,
  units,
  brands,
  categories,
  customers,
  locations,
  suppliers,
  diningAreas,
  diningTables,
  products,
  productCodes,
  productComponents,
  batches,
  purchaseRequests,
  purchaseRequestLines,
  purchaseOrders,
  purchaseOrderLines,
  goodsReceipts,
  goodsReceiptLines,
  documentApprovals,
  salesOrders,
  salesOrderLines,
  salesInvoices,
  salesInvoiceLines,
  creditNotes,
  creditNoteLines,
  invoiceLineProducts,
  productSerials,
  orders,
  orderItems,
  kotTickets,
  kotItems,
  payments,
  paymentAllocations,
  deliveries,
  supplierBills,
  supplierPayments,
  orderRequests,
  orderRequestItems,
  supplierCredits,
  supplierCreditLines,
  supplierAllocations,
  stockMovements,
  invoicePrints,
] as const

export type SyncedTable = (typeof syncedTables)[number]
export type TableName = SyncedTable['name']
export type TableByName<N extends TableName> = Extract<SyncedTable, { name: N }>

export const tablesByName = Object.fromEntries(syncedTables.map((t) => [t.name, t])) as {
  [N in TableName]: TableByName<N>
}
